import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../../utils/supabase';
import { useMerch } from '../../contexts/MerchContext';
import CartDrawer from './CartDrawer';
import Header from '../../components/ui/Header';
import { ShoppingBag, Loader } from 'lucide-react';

const MerchStore = () => {
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const { setIsCartOpen, cartCount } = useMerch();

    useEffect(() => {
        fetchProducts();
    }, []);

    const fetchProducts = async () => {
        try {
            const { data: { session } } = await supabase.auth.getSession();

            // Note: Adjust the endpoint if your Edge Function handles routing differently
            // Assuming merch-api handles default GET as fetching products or we need a specific path
            // For now, let's target the function root or a consistent path if previously working.
            // Based on previous context, we'll try hitting the function with a path parameter or just the root if it returns products by default.
            // Let's use the explicit /products path if the edge function supports it, otherwise fallback/debug.

            const functionUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/merch-api/products`;

            const res = await fetch(functionUrl, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${session?.access_token}`,
                    'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY
                }
            });

            const result = await res.json();

            // Handle different possible response structures from Printful proxy
            if (result.result && Array.isArray(result.result)) {
                setProducts(result.result);
            } else if (Array.isArray(result)) { // Some proxies return array directly
                setProducts(result);
            } else if (result.error) {
                console.error("API Error:", result.error);
            } else {
                // Fallback: check if 'data' or similar exists
                console.warn("Unexpected API response structure:", result);
            }
        } catch (error) {
            console.error('Error fetching products:', error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-background">
            <Header />
            <div className="pt-24 pb-12">
                <div className="container mx-auto px-4">
                    {/* Header */}
                    <div className="flex justify-between items-center mb-8">
                        <div>
                            <h1 className="text-3xl font-bold text-foreground">Solis Merch</h1>
                            <p className="text-muted-foreground">Official gear for the ultimate gamer.</p>
                        </div>
                        <button
                            onClick={() => setIsCartOpen(true)}
                            className="relative p-3 bg-card border border-border rounded-full hover:bg-muted transition-colors"
                        >
                            <ShoppingBag className="w-6 h-6 text-foreground" />
                            {cartCount > 0 && (
                                <span className="absolute -top-1 -right-1 bg-primary text-primary-foreground text-xs w-5 h-5 flex items-center justify-center rounded-full font-bold">
                                    {cartCount}
                                </span>
                            )}
                        </button>
                    </div>

                    {/* Product Grid */}
                    {loading ? (
                        <div className="flex justify-center items-center h-64">
                            <Loader className="w-8 h-8 text-primary animate-spin" />
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                            {products.map((product) => (
                                <Link to={`/merch/product/${product.id}`} key={product.id} className="group">
                                    <div className="bg-card border border-border rounded-xl overflow-hidden hover:shadow-lg transition-all duration-300">
                                        <div className="aspect-square bg-muted/50 relative overflow-hidden">
                                            <img
                                                src={product.thumbnail_url}
                                                alt={product.name}
                                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                            />
                                        </div>
                                        <div className="p-4">
                                            <h3 className="font-semibold text-lg text-foreground mb-1 line-clamp-1">{product.name}</h3>
                                            <p className="text-primary font-bold">
                                                View Details
                                            </p>
                                        </div>
                                    </div>
                                </Link>
                            ))}
                        </div>
                    )}
                </div>
            </div>
            <CartDrawer />
        </div>
    );
};

export default MerchStore;
